import SiteCoreLinks from "@/components/SiteCoreLinks";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { collectionPageSchema } from "@/lib/aeo";
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { VISA_TYPES, CATEGORIES, type VisaType, type VisaCategory } from "@/data/visa-types";
import { LeadFormModal } from "@/components/LeadFormModal";
import CompareTripleKeyLinks from "@/components/CompareTripleKeyLinks";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "เปรียบเทียบวีซ่าไทย 10 ประเภท — Non-B · LTR · Smart · Elite · DTV · Retirement · Marriage 2026";
const DESC =
  "เครื่องมือเทียบวีซ่าไทย Side-by-Side ครบ 10 ประเภท: ค่าธรรมเนียม · เงื่อนไขรายได้ · Work Permit · ระยะเวลา · เส้นทาง PR — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

export const Route = createFileRoute("/visa-comparison")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/visa-comparison", "th")},
      { name: "description", content: ctaDesc(DESC, "/visa-comparison", "th")},
      { name: "keywords", content: "เปรียบเทียบวีซ่าไทย, LTR vs Smart Visa, Elite Visa ไทย, DTV Digital Nomad, Retirement Visa Thailand, Non-B Marriage Visa" },
      { property: "og:title", content: ctaTitle(TITLE, "/visa-comparison", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/visa-comparison", "th")},
      { property: "og:url", content: `${SITE}/visa-comparison` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/visa-comparison` }, ...hreflangLinks("/visa-comparison")],
    scripts: [consolidateGraph([
      collectionPageSchema({ path: "/visa-comparison", name: TITLE, description: DESC, itemCount: VISA_TYPES.length }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "เปรียบเทียบวีซ่า", item: `${SITE}/visa-comparison` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "วีซ่าไทย 10 ประเภท สำหรับชาวต่างชาติ",
          numberOfItems: VISA_TYPES.length,
          itemListElement: VISA_TYPES.map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${v.code} — ${v.nameTh}`,
            description: v.tagline,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "LTR Visa ดีกว่า Elite Visa อย่างไร?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "LTR Visa (10 ปี) อนุญาตให้ทำงานในไทยได้และมีสิทธิประโยชน์ภาษี 17% สำหรับ Highly-Skilled Professionals ส่วน Elite Visa (5–20 ปี) เป็นวีซ่า Lifestyle ห้ามทำงาน เน้นความสะดวกสบายและบริการ VIP — เลือก LTR ถ้าต้องการทำงาน/ลดภาษี, Elite ถ้าต้องการ Lifestyle อย่างเดียว",
              },
            },
            {
              "@type": "Question",
              name: "DTV Visa ทำงานในไทยได้หรือไม่?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "DTV (Destination Thailand Visa) ห้ามรับงานในไทยโดยเด็ดขาด — อนุญาตเฉพาะ Remote Work ให้ลูกค้าต่างประเทศ หากต้องการทำงานบริษัทไทยต้องเปลี่ยนเป็น Non-B + Work Permit",
              },
            },
            {
              "@type": "Question",
              name: "วีซ่าเกษียณ O-A กับ O-X ต่างกันอย่างไร?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "O-A (1 ปี ต่อได้): เงินฝาก ฿800K หรือรายได้ ฿65K/เดือน ขอได้ทุกสัญชาติ · O-X (10 ปี 5+5): ต้องฝาก ฿3 ล้าน lock 1 ปี + ประกัน $100K เฉพาะ 14 สัญชาติ (UK/US/EU/JP/AU ฯลฯ)",
              },
            },
            {
              "@type": "Question",
              name: "Smart Visa ต้องทำ Work Permit แยกหรือไม่?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "ไม่ต้อง — Smart Visa อนุญาตให้ทำงานได้อัตโนมัติในสาขาที่ระบุ (S-Curve 13 อุตสาหกรรม) โดยไม่ต้องขอ Work Permit แยกจากกระทรวงแรงงาน",
              },
            },
            {
              "@type": "Question",
              name: "วีซ่าใดนับเวลาขอ Permanent Residence (PR) ได้?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "วีซ่าที่นับเวลาขอ PR (3 ปีติดต่อกัน) ได้แก่ Non-B, Smart Visa, Non-O Marriage, Non-O-A/O-X Retirement · ส่วน Elite Visa, DTV, ED Visa, Tourist ไม่นับเวลาขอ PR",
              },
            },
          ],
        }),
      },
    ])],
  }),
  component: VisaComparisonPage,
});

function VisaComparisonPage() {
  const [selectedCat, setSelectedCat] = useState<VisaCategory | "all">("all");
  const [compareIds, setCompareIds] = useState<string[]>(["non-b", "ltr-visa", "smart-visa"]);

  const filtered = useMemo(
    () => (selectedCat === "all" ? VISA_TYPES : VISA_TYPES.filter((v) => v.category === selectedCat)),
    [selectedCat],
  );

  const comparing = useMemo(
    () => compareIds.map((id) => VISA_TYPES.find((v) => v.id === id)).filter(Boolean) as VisaType[],
    [compareIds],
  );

  const toggleCompare = (id: string) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fafaf7] to-white">
      {/* Hero */}
      <section className="border-b border-amber-500/20 bg-navy-deep text-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <nav className="mb-4 text-xs text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-300">หน้าแรก</Link>
            <span className="mx-2">›</span>
            <span className="text-amber-300">เปรียบเทียบวีซ่า</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            🛂 Visa Comparison Tool · อัพเดต 2026
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            เปรียบเทียบวีซ่าไทย 10 ประเภท<br />
            <span className="text-amber-400">เลือกวีซ่าที่ใช่ใน 3 นาที</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            เครื่องมือเทียบ Side-by-Side — ค่าธรรมเนียม ทางการ · เงื่อนไขรายได้ · Work Permit · เส้นทาง PR ·
            สิทธิประโยชน์ทางภาษี <strong className="text-white">ข้อมูลตรงจากกฎหมาย ตม./BOI/MFA</strong> ปรับล่าสุดมกราคม 2026
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <LeadFormModal
              trigger={
                <button type="button" className="rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-navy-deep transition hover:bg-amber-400">
                  ✍️ ขอคำปรึกษาเลือกวีซ่า (ฟรี)
                </button>
              }
            />
            <a href="tel:+66832494999" className="rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10">
              📞 083-249-4999
            </a>
            <Link to="/quote" className="rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10">
              💰 คำนวณราคา
            </Link>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="sticky top-0 z-30 border-b border-black/5 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap gap-2 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={() => setSelectedCat("all")}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
              selectedCat === "all" ? "bg-navy-deep text-white" : "bg-black/5 text-navy-deep/70 hover:bg-black/10"
            }`}
          >
            ทั้งหมด ({VISA_TYPES.length})
          </button>
          {(Object.keys(CATEGORIES) as VisaCategory[]).map((cat) => {
            const meta = CATEGORIES[cat];
            const count = VISA_TYPES.filter((v) => v.category === cat).length;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCat(cat)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition ${
                  selectedCat === cat ? "bg-navy-deep text-white" : "bg-black/5 text-navy-deep/70 hover:bg-black/10"
                }`}
              >
                {meta.emoji} {meta.label} ({count})
              </button>
            );
          })}
          <div className="ml-auto text-xs text-navy-deep/60">
            กำลังเปรียบเทียบ <strong className="text-amber-700">{comparing.length}/4</strong>
          </div>
        </div>
      </section>

      {/* Visa cards grid — pick to compare */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <h2 className="text-xl font-extrabold text-navy-deep sm:text-2xl">
          เลือกวีซ่าที่ต้องการเปรียบเทียบ <span className="text-sm font-normal text-navy-deep/60">(สูงสุด 4 ประเภท)</span>
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((v) => {
            const isSelected = compareIds.includes(v.id);
            const meta = CATEGORIES[v.category];
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => toggleCompare(v.id)}
                className={`group relative overflow-hidden rounded-2xl border-2 bg-white p-5 text-left transition ${
                  isSelected ? "border-amber-500 shadow-lg shadow-amber-500/20" : "border-black/8 hover:border-amber-500/40 hover:shadow-md"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
                      {meta.emoji} {meta.label}
                    </span>
                    <h3 className="mt-2 text-base font-extrabold text-navy-deep">{v.code}</h3>
                    <p className="text-xs font-bold text-navy-deep/70">{v.nameTh}</p>
                  </div>
                  <div
                    className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border-2 transition ${
                      isSelected ? "border-amber-500 bg-amber-500 text-white" : "border-black/15 bg-white text-transparent"
                    }`}
                  >
                    ✓
                  </div>
                </div>
                <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-navy-deep/70">{v.tagline}</p>
                <div className="mt-4 flex items-center justify-between border-t border-dashed border-black/10 pt-3 text-[11px] text-navy-deep/60">
                  <span>⏱ {v.durationMax}</span>
                  <span className="font-bold text-amber-700">⭐ ความนิยม {v.popularity}/10</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      {comparing.length > 0 && (
        <section className="border-y border-amber-500/20 bg-amber-50/30">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
            <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">📊 ตารางเปรียบเทียบ Side-by-Side</h2>
            <p className="mt-1 text-sm text-navy-deep/70">เปรียบเทียบ {comparing.length} วีซ่าที่เลือก — เลื่อนแนวนอนบนมือถือ</p>

            <div className="mt-6 overflow-x-auto rounded-2xl border border-black/10 bg-white shadow-sm">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="bg-navy-deep text-white">
                    <th className="sticky left-0 z-10 bg-navy-deep px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      หัวข้อ
                    </th>
                    {comparing.map((v) => (
                      <th key={v.id} className="min-w-[200px] px-4 py-3 text-left">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">{CATEGORIES[v.category].emoji} {CATEGORIES[v.category].label}</div>
                        <div className="mt-0.5 text-sm font-extrabold">{v.code}</div>
                        <div className="text-xs font-normal text-white/70">{v.nameTh}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  {([
                    ["ระยะเวลาเริ่มต้น", "durationInitial"],
                    ["ระยะเวลาสูงสุด", "durationMax"],
                    ["ค่าธรรมเนียมราชการ", "govFee"],
                    ["ค่าบริการ NYC", "serviceFee"],
                    ["ระยะเวลาดำเนินการ", "processingTime"],
                    ["รายได้ขั้นต่ำ", "minIncome"],
                    ["เงินลงทุน/ฝาก", "minInvestment"],
                    ["Work Permit", "workPermit"],
                    ["ครอบครัวติดตาม", "familyDependents"],
                    ["รายงานตัว 90 วัน", "ninetyDayReport"],
                    ["Re-entry Permit", "reentryPermit"],
                    ["เส้นทางสู่ PR", "pathToPR"],
                    ["สิทธิประโยชน์ภาษี", "taxBenefit"],
                  ] as const).map(([label, key]) => (
                    <tr key={key} className="hover:bg-amber-50/40">
                      <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left text-xs font-bold text-navy-deep">
                        {label}
                      </th>
                      {comparing.map((v) => (
                        <td key={v.id} className="px-4 py-3 align-top text-xs text-navy-deep/85">
                          {v[key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-amber-50/60 hover:bg-amber-100/40">
                    <th className="sticky left-0 z-10 bg-amber-50/60 px-4 py-3 text-left text-xs font-bold text-navy-deep">
                      ✅ เหมาะกับใคร
                    </th>
                    {comparing.map((v) => (
                      <td key={v.id} className="px-4 py-3 align-top">
                        <ul className="space-y-1 text-xs text-navy-deep/85">
                          {v.bestFor.map((b, i) => (
                            <li key={i}>• {b}</li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-emerald-50/30">
                    <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left text-xs font-bold text-emerald-700">
                      ✚ ข้อดี
                    </th>
                    {comparing.map((v) => (
                      <td key={v.id} className="px-4 py-3 align-top">
                        <ul className="space-y-1 text-xs text-emerald-900/85">
                          {v.pros.map((p, i) => (
                            <li key={i}>• {p}</li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-rose-50/30">
                    <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left text-xs font-bold text-rose-700">
                      ✕ ข้อจำกัด
                    </th>
                    {comparing.map((v) => (
                      <td key={v.id} className="px-4 py-3 align-top">
                        <ul className="space-y-1 text-xs text-rose-900/80">
                          {v.cons.map((c, i) => (
                            <li key={i}>• {c}</li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-navy-deep/[0.03]">
                    <th className="sticky left-0 z-10 bg-navy-deep/[0.03] px-4 py-3 text-left text-xs font-bold text-navy-deep">
                      📄 เอกสารหลัก
                    </th>
                    {comparing.map((v) => (
                      <td key={v.id} className="px-4 py-3 align-top">
                        <ul className="space-y-1 text-xs text-navy-deep/80">
                          {v.documents.map((d, i) => (
                            <li key={i}>• {d}</li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left text-xs font-bold text-navy-deep">
                      ความยาก (1=ง่าย, 10=ยาก)
                    </th>
                    {comparing.map((v) => (
                      <td key={v.id} className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10">
                            <div
                              className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500"
                              style={{ width: `${v.difficulty * 10}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-navy-deep">{v.difficulty}/10</span>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Recommendation engine */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">🎯 คำแนะนำตามสถานการณ์</h2>
        <p className="mt-1 text-sm text-navy-deep/70">เลือกตามสถานการณ์ของคุณ — เราแนะนำวีซ่าที่ตรงที่สุด</p>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              icon: "👔",
              title: "พนักงานบริษัทไทย",
              detail: "นายจ้างจ่ายเงินเดือนผ่านบริษัทไทย",
              pick: "Non-B + Work Permit",
              id: "non-b",
              color: "from-blue-50 to-white border-blue-300",
            },
            {
              icon: "💼",
              title: "ผู้บริหาร/ผู้เชี่ยวชาญ S-Curve",
              detail: "AI, Biotech, Robotics, รายได้ ฿100K+/เดือน",
              pick: "Smart Visa (T/E)",
              id: "smart-visa",
              color: "from-purple-50 to-white border-purple-300",
            },
            {
              icon: "🌎",
              title: "Wealthy Global Citizen",
              detail: "รายได้ $80K/ปี + ลงทุน $500K",
              pick: "LTR Visa",
              id: "ltr-visa",
              color: "from-amber-50 to-white border-amber-400",
            },
            {
              icon: "💻",
              title: "Digital Nomad / Remote Worker",
              detail: "ทำงานให้ลูกค้าต่างประเทศ ไม่รับงานในไทย",
              pick: "DTV Visa",
              id: "dtv-visa",
              color: "from-sky-50 to-white border-sky-300",
            },
            {
              icon: "💞",
              title: "แต่งงานกับคนไทย",
              detail: "มีทะเบียนสมรส คร.2/คร.3",
              pick: "Marriage Visa (Non-O)",
              id: "marriage-visa",
              color: "from-rose-50 to-white border-rose-300",
            },
            {
              icon: "🌴",
              title: "เกษียณอายุ 50+",
              detail: "มีเงินบำนาญ หรือเงินฝาก ฿800K+",
              pick: "Non-O-A (1 ปี) หรือ O-X (10 ปี)",
              id: "retirement-o-a",
              color: "from-emerald-50 to-white border-emerald-300",
            },
            {
              icon: "👑",
              title: "ต้องการ Lifestyle + VIP",
              detail: "ไม่ต้องการพิสูจน์รายได้ ไม่ทำงาน",
              pick: "Elite Visa (TPVS)",
              id: "elite-visa",
              color: "from-yellow-50 to-white border-yellow-400",
            },
            {
              icon: "🎓",
              title: "นักเรียน/นักศึกษา",
              detail: "ลงทะเบียนสถาบัน MOE-Approved",
              pick: "ED Visa (Non-ED)",
              id: "ed-visa",
              color: "from-indigo-50 to-white border-indigo-300",
            },
            {
              icon: "✈️",
              title: "เยี่ยมเยือนระยะสั้น < 60 วัน",
              detail: "ท่องเที่ยว, business trip, family visit",
              pick: "Visa Exempt / TR",
              id: "tourist-tr",
              color: "from-slate-50 to-white border-slate-300",
            },
          ].map((rec) => (
            <button
              key={rec.id}
              type="button"
              onClick={() => {
                if (!compareIds.includes(rec.id)) toggleCompare(rec.id);
                document.querySelector("table")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`group rounded-2xl border-2 bg-gradient-to-br p-5 text-left transition hover:shadow-md ${rec.color}`}
            >
              <div className="text-3xl">{rec.icon}</div>
              <h3 className="mt-3 text-base font-extrabold text-navy-deep">{rec.title}</h3>
              <p className="mt-1 text-xs text-navy-deep/70">{rec.detail}</p>
              <div className="mt-4 border-t border-dashed border-black/15 pt-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">เราแนะนำ</div>
                <div className="mt-0.5 text-sm font-extrabold text-navy-deep">{rec.pick} →</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อยเรื่องวีซ่าไทย</h2>
        <div className="mt-6 divide-y divide-black/10 rounded-2xl border border-black/10 bg-white">
          {[
            { q: "LTR Visa ดีกว่า Elite Visa อย่างไร?", a: "LTR Visa (10 ปี) อนุญาตให้ทำงานในไทยได้และมีสิทธิประโยชน์ภาษี 17% สำหรับ Highly-Skilled Professionals · Elite Visa (5–20 ปี) เป็นวีซ่า Lifestyle ห้ามทำงาน เน้น VIP Service — เลือก LTR ถ้าต้องการทำงาน/ลดภาษี, Elite ถ้าต้องการ Lifestyle อย่างเดียว" },
            { q: "DTV Visa ทำงานในไทยได้หรือไม่?", a: "ห้ามรับงานในไทยโดยเด็ดขาด — อนุญาตเฉพาะ Remote Work ให้ลูกค้าต่างประเทศ หากต้องการทำงานบริษัทไทยต้องเปลี่ยนเป็น Non-B + Work Permit" },
            { q: "วีซ่าเกษียณ O-A กับ O-X ต่างกันอย่างไร?", a: "O-A (1 ปี ต่อได้): เงินฝาก ฿800K หรือรายได้ ฿65K/เดือน ขอได้ทุกสัญชาติ · O-X (10 ปี 5+5): ต้องฝาก ฿3 ล้าน lock 1 ปี + ประกัน $100K เฉพาะ 14 สัญชาติ (UK/US/EU/JP/AU ฯลฯ)" },
            { q: "Smart Visa ต้องทำ Work Permit แยกหรือไม่?", a: "ไม่ต้อง — Smart Visa อนุญาตให้ทำงานได้อัตโนมัติในสาขาที่ระบุ (S-Curve 13 อุตสาหกรรม) โดยไม่ต้องขอ Work Permit แยกจากกระทรวงแรงงาน" },
            { q: "วีซ่าใดนับเวลาขอ Permanent Residence (PR) ได้?", a: "ตาม พ.ร.บ.คนเข้าเมือง 2522 ม.41 + กฎกระทรวง 6/2547 ต้องถือวีซ่า Non-Immigrant ต่อเนื่อง ≥3 ปี (visa extension รายปี ไม่ใช่ re-entry) — เข้าเงื่อนไข: Non-B (ทำงาน), Smart Visa, Non-O Marriage, Non-O-A/O-X Retirement · ไม่นับเวลา: Elite/Privilege Visa, DTV, ED, Tourist, LTR (LTR เป็น 10-year resident แทน PR) — โควต้า PR 100 คน/สัญชาติ/ปี ค่าธรรมเนียม 191,400 บาท" },
            { q: "ถ้าเปลี่ยนนายจ้าง ต้องทำอย่างไร?", a: "Non-B ต้องยกเลิกวีซ่าเดิมและขอใหม่กับนายจ้างใหม่ภายใน 7 วัน · Smart Visa/LTR เปลี่ยนงานได้โดยไม่ต้องยื่นใหม่ (Digital Work Permit) แค่ Update ที่ BOI" },
          ].map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none text-sm font-bold text-navy-deep marker:hidden">
                <span className="mr-2 inline-block text-amber-600 transition group-open:rotate-90">▶</span>
                {f.q}
              </summary>
              <p className="mt-3 pl-6 text-sm leading-relaxed text-navy-deep/75">{f.a}</p>
            </details>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-navy-deep to-[#0a1a3d] p-8 text-center text-white">
          <h3 className="text-xl font-extrabold sm:text-2xl">ยังไม่แน่ใจเลือกวีซ่าไหน?</h3>
          <p className="mt-2 text-sm text-white/75">
            ทีมทนายความและที่ปรึกษาวีซ่า NYC Legal — ประเมินเคสฟรี ตอบใน 15 นาที (จันทร์–เสาร์ 09:00–18:00)
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button type="button" className="rounded-full bg-amber-500 px-6 py-3 text-sm font-extrabold text-navy-deep transition hover:bg-amber-400">
                  ✍️ ขอประเมินเคสฟรี
                </button>
              }
            />
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-[#06C755]/60 bg-[#04A03A]/20 px-6 py-3 text-sm font-bold text-white transition hover:bg-[#04A03A]/30"
            >
              💬 LINE @nycli
            </a>
            <Link to="/case-studies" className="rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10">
              ดู Case Studies →
            </Link>
          </div>
        </div>
      </section>

      <CompareTripleKeyLinks
        mode="th"
        topicTitleTh="วีซ่าไทย 10 ประเภท"
        topicTitleEn="Thailand Visa Comparison"
      />
          <SiteCoreLinks />
    </main>
  );
}
